import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    OnDestroy,
    Output,
    Signal,
    booleanAttribute,
    computed,
    inject,
    input,
} from "@angular/core";
import { Subscription } from "rxjs";

import {
    MediaPictureComponent,
    MediaQueryOption,
} from "@hermes/aphrodite-media-picture";
import { CTAAnchor, CTALink, type CTAToken } from "@hermes/api-model-common";
import { type Media } from "@hermes/api-model-editorial";

import { LOCALE } from "@hermes/core-app-core";
import {
    CtaContainerComponent,
    EditorialCTAResolverModule,
    resolveCTALinkOrCTAAnchor,
} from "@hermes/domains-edito-fragments-editorial-cta-resolver";

import {
    displayAriaLabel,
    getMediaName,
    setMediaImageAltFromCTA,
    shouldDisplayOverlayText,
    showFigureCaption,
} from "../../helpers/media.helpers";
import { MediaCaptionsComponent } from "../media-captions/media-captions.component";

/**
 * Component is used to display a media that we know won't be a video (instead of h-media-cta)
 * It's only purpose is to not include the video.component files in the main bundle to reduce it
 * There should be a study to use h-media-cta with a conditional video component loading to avoid duplication present in this component
 */
@Component({
    selector: "h-media-picture-cta",
    imports: [
        CommonModule,
        EditorialCTAResolverModule,
        MediaPictureComponent,
        MediaCaptionsComponent,
        CtaContainerComponent,
    ],
    templateUrl: "./media-picture-cta.component.html",
    styleUrls: ["./media-picture-cta.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaPictureCtaComponent implements OnDestroy {
    public locale = inject(LOCALE);

    public mediaOptions = input<MediaQueryOption[]>();

    public preload = input(false, {
        transform: booleanAttribute,
    });

    /**
     * Determines whether this component sends analytics events upon click
     * (i.e., when calling `mediaRedirection`).
     *
     * If set to `false`, analytics will only be sent from parent components,
     * and not from this component itself.
     *
     * Defaults to `true`.
     */
    public shouldSendDefaultRedirectionAnalytics = input(true, {
        transform: booleanAttribute,
    });

    public inputMedia = input.required<Media>({
        alias: "media",
    });

    public title = input<string>();

    public token = input<CTAToken>();

    @Output() public readonly mediaRedirectionEvent =
        new EventEmitter<CTALink>();

    public subscription: Subscription = new Subscription();

    public mediaName: Signal<string> = computed(() =>
        getMediaName(this.media()),
    );
    public showFigureCaption: Signal<boolean> = computed(() =>
        showFigureCaption(this.media(), this.locale),
    );
    public inputCta: Signal<CTALink | CTAAnchor | undefined> = computed(() => {
        const token = this.token();
        return token ? resolveCTALinkOrCTAAnchor(token) : undefined;
    });

    public mediaCta = computed<CTALink | CTAAnchor | undefined>(() => {
        const mediaToken = this.inputMedia().token;
        return mediaToken ? resolveCTALinkOrCTAAnchor(mediaToken) : undefined;
    });

    public cta = computed<CTALink | CTAAnchor | undefined>(
        () => this.inputCta() ?? this.mediaCta() ?? undefined,
    );

    public media: Signal<Media> = computed(() =>
        setMediaImageAltFromCTA(this.inputMedia(), this.cta()),
    );
    public ariaLabel: Signal<string | undefined> = computed(() =>
        displayAriaLabel(this.media(), this.title()),
    );

    public hasOverlayText: Signal<boolean> = computed(() => {
        const media = this.media();
        return shouldDisplayOverlayText(media);
    });

    public linkAriaLabel: Signal<string | undefined> = computed(() => {
        const mediaTitle = this.title();
        return mediaTitle
            ? $localize`:@@hermes.category_push.aria_label:Voir la catégorie ${mediaTitle}:INTERPOLATION:`
            : undefined;
    });

    public mediaRedirection(event: CTALink): void {
        this.mediaRedirectionEvent.emit(event);
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
